import React, { useState } from "react";
import { Search, Filter, PlusCircle } from "lucide-react";
import accessManagementService from "../../services/accessManagementService";
import { useQuery } from "@tanstack/react-query";
import EditMemberDialog from "./EditMemberDialog";
import { DeleteUserMutation } from "../../mutations/AccessManagementMutationHook";
import { useToast } from "../../context/ToastContext";
import heartLoading from "../../assests/heartLoading.gif";

// MUI Dialog Imports
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import MuiButton from '@mui/material/Button'; // Aliased to avoid conflict if PrimeReact Button is used elsewhere

// MUI Table Imports
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';


const UserManagement = ({ setEnableNewMemberPopUp }) => {

  const {
    data: allUserData = [],
    isLoading: isAllUserDataLoading,
    isFetching: isAllUserDataFetching,
  } = useQuery({
    queryKey: ["getAllUserList"],
    queryFn: () => accessManagementService.getAllUser(),
  });

  const toast = useToast();

  const { mutate: deleteUser, isPending: isDeleting } = DeleteUserMutation(
    () => {
      toast({ severity: "info", summary: "Confirmed", detail: "User Deleted Successfully.", life: 3000 });
      handleCloseDeleteDialog(); // Close MUI dialog on success
    },
    (error) => {
      toast({ severity: "error", summary: "Error", detail: error.response.data.detail || error.message, life: 3000 });
      handleCloseDeleteDialog(); // Close MUI dialog on error
    }
  );

  // State and Handlers for MUI Delete Dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setUserToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      deleteUser([userToDelete.user_id]);
      // Dialog will be closed by the deleteUser mutation's callbacks (onSuccess/onError)
    }
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedView, setSelectedView] = useState("Members");
  const [currentPage, setCurrentPage] = useState(1);
  const [enableEditMemberPopUp, setEnableEditMemberPopUp] = useState(false);
  const [editUserData, setEditUserData] = useState(null);
  const [filterDomain, setFilterDomain] = useState("");
  const [filterSubDomain, setFilterSubDomain] = useState("");
  const [filterSubSubDomain, setFilterSubSubDomain] = useState("");
  const [filterProduct, setFilterProduct] = useState("");
  const [filterRoleName, setFilterRoleName] = useState("");

  const rowsPerPage = 10;

  if (isAllUserDataLoading || isAllUserDataFetching) {
    return (
      <div className="flex items-center justify-center min-h-[90vh]">
        <div className="flex flex-col items-center space-y-3">
          <img src={heartLoading} alt="Loading..." width={200} />
          <span className="text-gray-700 text-[20px] text-center">
            Hang tight! The resources are loading...
          </span>
        </div>
      </div>
    );
  }

  const landingPage = {
    userList:
      allUserData
    ,
    allDataAssets: [],
  };

  const unfilteredData =
    selectedView === "Members" ? landingPage.userList : landingPage.allDataAssets;

  const dataSource = unfilteredData.filter((item) => {
    const domainMatch = filterDomain ? item.domains?.some(d => d.parent_domain === filterDomain) : true;
    const subDomainMatch = filterSubDomain ? item.domains?.some(d => d.domain === filterSubDomain) : true;
    const subSubDomainMatch = filterSubSubDomain ? item.domains?.some(d => d.subdomains.includes(filterSubSubDomain)) : true;
    const productMatch = filterProduct ? item.product_names?.some(p => p === filterProduct) : true;
    const roleMatch = filterRoleName ? item.role_name === filterRoleName : true;
    const searchMatch = searchQuery
      ? item.user_email.toLowerCase().includes(searchQuery.toLowerCase())
      : true;

    return (
      domainMatch &&
      subDomainMatch &&
      subSubDomainMatch &&
      productMatch &&
      roleMatch &&
      searchMatch
    );
  });

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = dataSource.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(dataSource.length / rowsPerPage);

  const parentDomainList = allUserData.filter(item => item.domains?.length > 0)
                    .flatMap(item => item.domains.map(dom => dom.parent_domain))
                    .filter((value, index, self) => self.indexOf(value) === index);

  const domainList = allUserData.filter(item => item.domains?.length > 0)
                    .flatMap(item => item.domains.map(dom => dom.domain))
                    .filter((value, index, self) => self.indexOf(value) === index);

  const subDomainList = allUserData.filter(item => item.domains?.length > 0)
                    .flatMap(item => item.domains.flatMap(domain => domain.subdomains))
                    .filter((value, index, self) => self.indexOf(value) === index);
                    
  //const productList = allUserData? [...new Set(allUserData.map(item => item.product_names).filter(Boolean))] : [];
  const productList = allUserData.filter(item => item.product_names?.length > 0)
                    .flatMap(item => item.product_names)
                    .filter((value, index, self) => self.indexOf(value) === index);

  const roleNameList = allUserData ? [...new Set(allUserData.map(item => item.role_name).filter(Boolean))] : [];

  return (
    <div style={{  width: "100%", maxWidth: "100%", overflow: "hidden" }} className="dashboard-container p-2">
      <div className="dashboard-main" style={{ width: "100%", maxWidth: "100%", overflow: "auto" }}>
        <div className="dashboard-header">
          <div className="dashboard-actions">
         <div className="flex gap-2 items-center">
         <div onClick={() => setEnableNewMemberPopUp(true)} className="onboard-button h-[50px]">
              <PlusCircle className="icon" />
              <span>Add New Members</span>
            </div>
            <button
                className="filter-button h-[50px]"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="icon" />
                <span>Filter</span>
              </button>
         </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div className="search-box h-[50px]">
                <Search className="search-icon" />
                <input
                  type="text"
                  placeholder="Search for members"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
            </div>

           
          </div>

          {showFilters && (
            <div className="filters-panel">
              <div className="filter-group">
                <label>Parent Domain</label>
                <select
                  value={filterDomain}
                  onChange={(e) => setFilterDomain(e.target.value)}
                >
                  <option value="">All</option>
                   {parentDomainList.map((domain, index) => (
                    <option key={index} value={domain}>{domain}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Domain</label>
                <select
                  value={filterSubDomain}
                  onChange={(e) => setFilterSubDomain(e.target.value)}
                >
                  <option value="">All</option>
                   {domainList.map((domain, index) => (
                    <option key={index} value={domain}>{domain}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Sub Domain</label>
                <select
                  value={filterSubSubDomain}
                  onChange={(e) => setFilterSubSubDomain(e.target.value)}
                >
                  <option value="">All</option>
                  {subDomainList.map((domain, index) => (
                    <option key={index} value={domain}>{domain}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Product</label>
                <select
                  value={filterProduct}
                  onChange={(e) => setFilterProduct(e.target.value)}
                >
                  <option value="">All</option>
                  {productList.map((product, index) => (
                    <option key={index} value={product}>{product}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Role Name</label>
                <select
                  value={filterRoleName}
                  onChange={(e) => setFilterRoleName(e.target.value)}
                >
                  <option value="">All</option>
                  {roleNameList.map((role, index) => (
                    <option key={index} value={role}>{role}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

        </div>
        <div className="pagination" style={{ maxWidth: 'calc(100% - 20px)' }}>
          <span className="text-[16px]" >
           Total Users : {dataSource?.length || 0}
          </span>
          <div className="pagination-controls">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, idx) => (
              <button
                key={idx}
                className={currentPage === idx + 1 ? "active" : ""}
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </button>
            ))}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        </div>

          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 'none',
              marginLeft: '10px',
              marginRight: '10px',
              width: 'calc(100% - 20px)',
              maxWidth: '100%',
              border: '1px solid #d1d5db',
              borderRadius: '12px',
              overflow: 'hidden',
              marginTop: "10px"
            }}
            className="font-poppins"
          >
            <Table sx={{ minWidth: 650, width: '100%', '& tbody .MuiTableCell-root': { borderBottom: 'none' } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#F6F6F6" }}>
                  {[
                    "User Email",
                    "Parent Domain",
                    "Domain",
                    "Sub Domain",
                    "Product",
                    "Role Name",
                    "Actions",
                  ].map((col, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "#404040",
                        textTransform: 'uppercase'
                      }}
                    >
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRows.map((table, index) => (
                  <TableRow
                    key={table.user_id}
                    sx={{
                      border: 'none',
                      fontSize: '12px'
                    }}
                  >
                    <TableCell sx={{
                      fontWeight: 500,
                      fontSize: '12px',
                      maxWidth: '250px',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal'
                    }}>
                      {table.user_email}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: '12px',
                      maxWidth: '200px',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal'
                    }}>
                      {table.domains?.map((d) => d.parent_domain).join(', ')}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: '12px',
                      maxWidth: '200px',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal'
                    }}>
                      {table.domains?.map((d) => d.domain).join(', ')}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: '12px',
                      maxWidth: '200px',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal'
                    }}>
                      {table.domains?.flatMap((d) => d.subdomains).join(', ')}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: '12px',
                      maxWidth: '200px',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal'
                    }}>
                      {table.product_names?.join(', ')}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: '12px',
                      maxWidth: '150px',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal'
                    }}>
                      {table.role_name}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: '12px',
                      minWidth: '100px'
                    }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <button 
                          className="bg-[#bb1ccc] text-white p-2 text-[12px] border rounded hover:bg-[#000080] transition-colors"
                          onClick={() => { setEditUserData(table); setEnableEditMemberPopUp(true); }}
                        >
                          Edit
                        </button>
                        <button 
                          className="bg-red-100 text-red-600 p-2 text-[12px] border rounded hover:bg-red-200 transition-colors"
                          onClick={() => handleOpenDeleteDialog(table)}
                        >
                          Delete
                        </button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <div className="pagination" style={{ marginLeft: '10px', marginRight: '10px', maxWidth: 'calc(100% - 20px)' }}>
          <span>
            Showing {indexOfFirstRow + 1} to{" "}
            {Math.min(indexOfLastRow, dataSource.length)} of {dataSource.length}{" "}
            entries
          </span>
          <div className="pagination-controls">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, idx) => (
              <button
                key={idx}
                className={currentPage === idx + 1 ? "active" : ""}
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </button>
            ))}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        </div>

       
      </div>
      {/* PrimeReact ConfirmDialog was here, now replaced by MUI Dialog below */}
      {/* <ConfirmDialog /> */}

      {/* MUI Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle 
          id="alert-dialog-title"
          sx={{
            fontWeight: 700,
            fontSize: '18px',
            color: '#bb1ccc',
            borderBottom: '1px solid #e0e0e0',
            padding: '20px 24px 16px 24px'
          }}
        >
          Confirm Deletion
        </DialogTitle>
        <DialogContent sx={{ padding: '20px 24px' }}>
          <DialogContentText 
            id="alert-dialog-description"
            sx={{
              fontSize: '14px',
              color: '#555',
              lineHeight: '1.5'
            }}
          >
            Are you sure you want to delete <strong>{userToDelete?.user_email}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px 20px 24px', gap: 1 }}>
          <MuiButton 
            onClick={handleCloseDeleteDialog} 
            variant="outlined"
            sx={{
              borderColor: '#bb1ccc',
              color: '#bb1ccc',
              '&:hover': {
                borderColor: '#000080',
                backgroundColor: '#f0f8ff'
              }
            }}
          >
            Cancel
          </MuiButton>
          <MuiButton 
            onClick={handleConfirmDelete} 
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              '&:hover': {
                backgroundColor: '#b71c1c'
              }
            }}
            autoFocus
          >
            Delete
          </MuiButton>
        </DialogActions>
      </Dialog>

      {enableEditMemberPopUp && editUserData && (
        <EditMemberDialog onHide={() => setEnableEditMemberPopUp(false)} userData={editUserData} />
      )}
    </div>
  );
};

export default UserManagement;
